import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { graphql, Link, useStaticQuery } from 'gatsby';

import EmbiqWhiteLogo from '@images/logo-embiq-white.svg';
import EmbiqBlackLogo from '@images/logo_embiq-black.svg';
import ConsultationIcon from '@images/free_consultation_icon.svg';
import ConsultationWhiteIcon from '@images/free-consultation-icon-light.svg';
import { NavSubmenu } from './components';
import useClickOutside from '@utils/hooks/useClickOutside';
import { NavigationProps } from './NavigationProps';

import * as styles from './Navigation.module.scss';

export const Navigation: React.FC<NavigationProps> = ({ scheme }) => {
	const [isOpen, setOpen] = useState<boolean>(false);
	const [path, setPath] = useState<string>(null);
	const menuRef = useRef<HTMLUListElement | null>(null);
	const menuButtonRef = useRef<HTMLDivElement | null>(null);
	const EmbiqLogo = scheme === 'light' ? EmbiqBlackLogo : EmbiqWhiteLogo;
	const ConsultationLogo =
		scheme === 'light' ? ConsultationWhiteIcon : ConsultationIcon;

	const redirect = useMemo(() => {
		if (path) {
			if (path.startsWith('careers/') && path !== 'careers/') {
				return '/contact';
			}
		}
		return '#free-anchor';
	}, [path]);

	useClickOutside({
		ref: menuRef,
		callback: () =>
			setTimeout(() => {
				setOpen(false);
			}, 50),
		disabled: !isOpen,
		exceptionsRefs: [menuButtonRef],
	});

	const { site } = useStaticQuery(graphql`
		query menuQuery {
			site {
				siteMetadata {
					menuLinks {
						name
						url
						child {
							name
							url
						}
						sections {
							sectionName
							links {
								name
								url
								icon
							}
						}
					}
				}
			}
		}
	`);

	useEffect(() => {
		const url = location.pathname.slice(1);
		setPath(url);
	}, []);

	const handleOpenMenu = (): void => {
		setOpen(!isOpen);
	};

	return (
		<>
			{isOpen && <div className={styles.clickBlockade} />}
			<header className={classNames(styles.header, styles[scheme])}>
				<nav
					className={classNames(
						styles.nav,
						styles[scheme],
						isOpen && styles.show
					)}
				>
					<div
						ref={menuButtonRef}
						className={styles.hamburgerMenu}
						onClick={handleOpenMenu}
					>
						<div className={classNames(styles.line, styles.line1)}></div>
						<div className={classNames(styles.line, styles.line2)}></div>
						<div className={classNames(styles.line, styles.line3)}></div>
					</div>
					<Link to="/">
						<img
							src={EmbiqLogo}
							className={styles.logo}
							alt="Embiq logo"
							loading="lazy"
						/>
					</Link>
					<Link to={redirect}>
						<img
							className={styles.consultationLogo}
							src={ConsultationLogo}
							alt="Consultation logo"
							loading="lazy"
						/>
					</Link>
					<ul ref={menuRef} className={styles.menuList}>
						{site.siteMetadata.menuLinks.map((item) => (
							<li key={`${item.name + item.url}`}>
								<Link
									className={classNames(styles.main, styles.menuLink)}
									to={item.url}
									activeClassName={styles.activeLink}
									partiallyActive={true}
								>
									{item.name}
								</Link>
								<NavSubmenu menuItem={item} />
							</li>
						))}
						<Link className={styles.menuLink} to={redirect}>
							<li className={styles.consultation}>Beschreiben Sie das Projekt!</li>
						</Link>
					</ul>
				</nav>
			</header>
		</>
	);
};
